import { SomethingWentWrongFallbackComponent } from '../../error-boundary/SomethingWentWrongFallbackComponent';
import React, { useCallback } from 'react';
import { BulkProxySection } from '../BulkProxySection';
import styled from 'styled-components';
import EmptyState from '../../empty-state/EmptyState';
import type { FundToBulkProxy, FundToProxy } from '../../modals/pickers/types';
import { type ProxyMetaWithName } from '../../modals/pickers/proxy-picker/BulkProxyPicker';
import type { DetailedProxyMetadata, RangeAnalysisResponse, SimpleFund, SubjectRangeAnalysis } from 'venn-api';
import { withErrorBoundary } from '../../error-boundary/CustomizableErrorBoundary';
import type { BulkManagementTableModalManager } from '../types';
import { ProxyPickerSidePanel } from './ProxyPickerSidePanel';
import { assertNotNil } from 'venn-utils';
import type { LayeredDetailedProxyMetadata } from '../../hooks/useProxyDataQuery';

interface BulkManagementTableRootProps {
  modals: BulkManagementTableModalManager;
  openedSecondaryId: string | undefined;
  uniqFunds: SimpleFund[];
  selectedFunds: SimpleFund[];
  onProxyChange:
    | ((appliedProxies: ProxyMetaWithName[], failedToProxyFunds: (FundToProxy | FundToBulkProxy)[]) => void)
    | undefined;
  disableSelection: boolean;
  rangeAnalysis: RangeAnalysisResponse | undefined;
  proxyDataByFund: Record<string, LayeredDetailedProxyMetadata>;
  holdingsProxyDataByFund: Record<string, DetailedProxyMetadata | undefined>;
  rangeDataByFund: Record<string, SubjectRangeAnalysis[]>;
  onClearSelected: () => void;
  children: React.ReactNode;
}

export const BulkManagementTablePrimitivesRoot = withErrorBoundary(
  SomethingWentWrongFallbackComponent,
  InternalBulkManagementTablePrimitivesRoot,
);

function InternalBulkManagementTablePrimitivesRoot({
  modals,
  openedSecondaryId,
  uniqFunds,
  selectedFunds,
  onProxyChange,
  disableSelection,
  rangeAnalysis,
  proxyDataByFund,
  holdingsProxyDataByFund,
  rangeDataByFund,
  onClearSelected,
  children,
}: BulkManagementTableRootProps) {
  const handleClose = useCallback(() => modals.close(), [modals]);
  const onReturnsProxyPickerOpen = useCallback(() => modals.open('returnsProxy'), [modals]);
  const onHoldingsProxyPickerOpen = useCallback(() => modals.open('holdingsProxy'), [modals]);

  if (uniqFunds.length === 0) {
    return <EmptyState header="There are no investments in this portfolio." />;
  }

  return (
    <>
      <ProxyPickerSidePanel
        handleClose={handleClose}
        activeModal={modals.activeModal}
        onProxyChange={onProxyChange}
        investments={
          openedSecondaryId ? [assertNotNil(uniqFunds.find((f) => f.id === openedSecondaryId))] : selectedFunds
        }
      />
      <Wrapper>
        {children}
        {!disableSelection && selectedFunds.length > 0 && rangeAnalysis && (
          <BulkProxySection
            selectedFunds={selectedFunds}
            proxyDataByFund={proxyDataByFund}
            holdingsProxyDataByFund={holdingsProxyDataByFund}
            rangeDataByFund={Object.fromEntries(
              Object.entries(rangeDataByFund).map(([k, arr]) => [k, arr[arr.length - 1]]),
            )}
            onClearSelected={onClearSelected}
            onReturnsProxyPickerOpen={onReturnsProxyPickerOpen}
            onHoldingsProxyPickerOpen={onHoldingsProxyPickerOpen}
          />
        )}
      </Wrapper>
    </>
  );
}

const Wrapper = styled.div`
  width: 100%;
  position: relative;

  .bulk-management-table-wrapper,
  & > div {
    width: 100%;
  }
`;
